// Secondary action dropdown surfaced by the message-interaction
// panel's `⋮` Menu chip — Figma `2804:27483 → Action menu`.
//
// Five options stacked vertically:
//   Copy
//   Reply in Thread
//   Forward
//   Pin
//   Remove (destructive — text in #FF5353)
//
// Anchored to the menu trigger via the shared `Popover` component so
// it auto-flips, clamps to the viewport, and portals to `document.body`
// on react-native-web. Outside-press closes the popover.

import React from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import {
  ClipboardCopy,
  Bookmark,
  Forward,
  type LucideIcon,
  MessageSquare,
  Pin as PinIcon,
  Star,
  Trash2,
} from 'lucide-react-native';
import { tokens } from '../design-system/tokens';

export type MessageMenuAction =
  | 'copy'
  | 'replyInThread'
  | 'forward'
  | 'pin'
  | 'save'
  | 'star'
  | 'remove';

export interface MessageActionMenuProps {
  readonly onSelect: (action: MessageMenuAction) => void;
  /** Hide the Copy entry on tiles that don't have a textual body
   *  (image, voice, etc). Defaults to true. */
  readonly canCopy?: boolean | undefined;
  /** When false, the bubble is already pinned — switch the entry to
   *  "Unpin" text. */
  readonly isPinned?: boolean | undefined;
  /** When true, the bubble is already saved — switch the entry to
   *  "Unsave" text. */
  readonly isSaved?: boolean | undefined;
  /** When true, the bubble is already starred — switch the entry to
   *  "Unstar" text. */
  readonly isStarred?: boolean | undefined;
  /** Hide the "Reply in Thread" entry — used INSIDE a thread, where a
   *  message can't spawn another nested thread. Defaults to false. */
  readonly hideReplyInThread?: boolean | undefined;
  readonly testID?: string | undefined;
}

interface MenuItem {
  readonly action: MessageMenuAction;
  readonly label: string;
  // `LucideIcon` is `ForwardRefExoticComponent` with extra props
  // beyond size/strokeWidth/color — keep the existing icon type so
  // the renderer matches what `lucide-react-native` exports.
  readonly Icon: LucideIcon;
  readonly destructive?: boolean;
}

export const MessageActionMenu = React.memo<MessageActionMenuProps>(
  ({
    onSelect,
    canCopy = true,
    isPinned = false,
    isSaved = false,
    isStarred = false,
    hideReplyInThread = false,
    testID,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' =
      scheme === 'dark' ? 'dark' : 'light';

    const PANEL_FILL = colorScheme === 'dark' ? '#15171C' : '#FFFFFF';
    const LABEL_COLOR = colorScheme === 'dark' ? '#F5F6F8' : '#0B0C0F';
    const DESTRUCTIVE_COLOR = '#FF5353';
    const ICON_COLOR = LABEL_COLOR;

    const items: ReadonlyArray<MenuItem> = [
      ...(canCopy
        ? [{ action: 'copy' as const, label: 'Copy', Icon: ClipboardCopy }]
        : []),
      ...(hideReplyInThread
        ? []
        : [
            {
              action: 'replyInThread' as const,
              label: 'Reply in Thread',
              Icon: MessageSquare,
            },
          ]),
      { action: 'forward' as const, label: 'Forward', Icon: Forward },
      {
        action: 'pin' as const,
        label: isPinned ? 'Unpin' : 'Pin',
        Icon: PinIcon,
      },
      {
        action: 'save' as const,
        label: isSaved ? 'Unsave' : 'Save',
        Icon: Bookmark,
      },
      {
        action: 'star' as const,
        label: isStarred ? 'Unstar' : 'Star',
        Icon: Star,
      },
      {
        action: 'remove' as const,
        label: 'Remove',
        Icon: Trash2,
        destructive: true,
      },
    ];

    return (
      <View
        style={[
          styles.panel,
          { backgroundColor: PANEL_FILL, borderColor: '#2F333B' },
        ]}
        accessibilityLabel="Message action menu"
        testID={testID}
      >
        {items.map(({ action, label, Icon, destructive }) => {
          const labelStyle: TextStyle = {
            color: destructive ? DESTRUCTIVE_COLOR : LABEL_COLOR,
            fontFamily: tokens.typography.fontFamily.base,
            fontSize: 14,
            fontWeight: '400',
            lineHeight: 20,
          };
          return (
            <Pressable
              key={action}
              onPress={() => onSelect(action)}
              accessibilityRole="button"
              accessibilityLabel={label}
              style={({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => [
                styles.option,
                hovered === true || pressed === true
                  ? {
                      backgroundColor:
                        colorScheme === 'dark' ? '#262626' : '#F3F4F6',
                    }
                  : null,
              ]}
            >
              <Icon
                size={20}
                strokeWidth={1.75}
                color={destructive ? DESTRUCTIVE_COLOR : ICON_COLOR}
              />
              <Text style={labelStyle}>{label}</Text>
            </Pressable>
          );
        })}
      </View>
    );
  },
);

MessageActionMenu.displayName = 'MessageActionMenu';

const styles = StyleSheet.create({
  // Figma `Action menu`: r=12 padding 4/4/4/4 gap 4 width ~204
  panel: {
    minWidth: 204,
    flexDirection: 'column',
    gap: 4,
    padding: 4,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.24,
    shadowRadius: 18,
    elevation: 8,
  },
  // Figma `option`: r=8 padding 8/8/8/8 gap 12 width fill height 36
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 8,
  },
});
